import {
  IdentityConflictError,
  identityConflictFromBody,
  type IdentityDecision,
  type RowIdentity,
  type SyncStats,
} from "lib/api/rowIdentity";
import axios from "axios";
import { axiosInstance, dataServiceApi } from "contexts/axiosApiInstance";
import { createLogger } from "debug/DebugingLogger";
import { serverRefusalMessage } from "@/lib/serverError";
import type {
  BucketProviderKind,
  BucketScope,
} from "../Postgres/savedConnectionsApi";

const log = createLogger("bucketApi");

export interface ConnectBucketRequest {
  /** D24: org to mint into. Omitted = the caller's personal org. */
  org_id?: string;
  display_name: string;
  scope: BucketScope;
  provider: ConnectBucketProvider;
}

export type ConnectBucketProvider = { provider: "gcs" } & ConnectGcsFields;

export interface ConnectGcsFields {
  auth: ConnectGcsAuth;
}

/** GCS auth — two mechanisms, both keyed to a Google OAuth saved-connection.
 *
 *  - `user_oauth_direct` (fast path): the user's own OAuth access token (with
 *    the `devstorage.read_only` scope) reads GCS directly. Bucket-scoped only.
 *    Simple, but needs the sensitive scope on the consent screen.
 *  - `user_oauth_federation` (WIF): the id-token is exchanged at Google STS
 *    against the customer's WIF audience; no sensitive scope, supports account
 *    scope, but more setup. */
export type ConnectGcsAuth =
  | {
      kind: "user_oauth_direct";
      google_saved_connection_id: string;
    }
  | {
      kind: "user_oauth_federation";
      audience: string;
      google_saved_connection_id: string;
    };

export interface ConnectBucketResponse {
  id: string;
  display_name: string;
  scope: BucketScope;
  provider: BucketProviderKind;
}

const BASE = "/api/buckets";

export const connectBucket = async (
  body: ConnectBucketRequest,
): Promise<ConnectBucketResponse> => {
  try {
    const res = await axiosInstance.post<ConnectBucketResponse>(
      `${BASE}/connect`,
      body,
    );
    return res.data;
  } catch (error) {
    log.error("connectBucket failed:%o", error);
    throw mapError(error, "Failed to create bucket connection");
  }
};

/** Public, unauthenticated config the customer's GCP admin needs to wire up
 *  Workload Identity Federation. `allowed_audience` is MyCorr's OAuth
 *  client_id for THIS deployment (Google sets it as the id-token `aud`), so
 *  the value is always correct per environment — see
 *  `server/src/api/federation.rs`. */
export interface FederationConfig {
  issuer: string;
  /** MyCorr's OAuth client_id — paste into `--allowed-audiences`. */
  allowed_audience: string;
  claim_mapping_suggestion: {
    subject: string;
    email: string;
    org: string;
  };
  environment: string;
}

export const getFederationConfig = async (): Promise<FederationConfig> => {
  try {
    const res = await axiosInstance.get<FederationConfig>(
      "/api/public/federation/config",
    );
    return res.data;
  } catch (error) {
    log.error("getFederationConfig failed:%o", error);
    throw mapError(error, "Failed to load federation config");
  }
};

export interface BucketSummary {
  name: string;
  region: string | null;
  created_at: string | null;
}

export type BucketEntryKind = "file" | "prefix";

export interface BucketEntry {
  /** Full path within the bucket. Prefix entries keep their trailing slash. */
  path: string;
  kind: BucketEntryKind;
  size_bytes: number | null;
  etag: string | null;
  last_modified: string | null;
}

export interface ListAccountBucketsParams {
  pageSize?: number;
  pageToken?: string;
}

export interface ListAccountBucketsPage {
  buckets: BucketSummary[];
  nextToken: string | null;
}

/** Account-scope only — bucket-scoped connections return 400. */
export const listAccountBuckets = async (
  connectionId: string,
  params: ListAccountBucketsParams = {},
): Promise<ListAccountBucketsPage> => {
  try {
    const res = await axiosInstance.get<{
      buckets: BucketSummary[];
      next_token: string | null;
    }>(`${BASE}/${encodeURIComponent(connectionId)}/buckets`, {
      params: {
        page_size: params.pageSize,
        page_token: params.pageToken,
      },
    });
    return {
      buckets: res.data.buckets,
      nextToken: res.data.next_token,
    };
  } catch (error) {
    log.error("listAccountBuckets failed:%o", error);
    throw mapError(error, "Failed to list buckets in account");
  }
};

export interface ListObjectsParams {
  /** Required for account-scoped connections; ignored for bucket-scoped. */
  bucket?: string;
  prefix?: string;
  /** GCS glob — when set, listing is recursive and files-only. */
  matchGlob?: string;
  pageSize?: number;
  pageToken?: string;
}

export interface ListObjectsPage {
  entries: BucketEntry[];
  nextToken: string | null;
}

export const listObjects = async (
  connectionId: string,
  params: ListObjectsParams,
): Promise<ListObjectsPage> => {
  try {
    const res = await axiosInstance.get<{
      entries: BucketEntry[];
      next_token: string | null;
    }>(`${BASE}/${encodeURIComponent(connectionId)}/list`, {
      params: {
        bucket: params.bucket,
        prefix: params.prefix,
        match_glob: params.matchGlob,
        page_size: params.pageSize,
        page_token: params.pageToken,
      },
    });
    return {
      entries: res.data.entries,
      nextToken: res.data.next_token,
    };
  } catch (error) {
    log.error("listObjects failed:%o", error);
    throw mapError(error, "Failed to list objects in bucket");
  }
};

export type BucketFormat = "csv" | "tsv" | "parquet";

export type BucketProvider = "gcs";

/** Wire shape of `TableSource::Bucket(BucketObject)` — field names match
 *  the Rust `BucketObject` serde shape verbatim. */
export interface BucketObjectWire {
  provider: BucketProvider;
  bucket: string;
  object_path: string;
  format: BucketFormat;
  size_bytes: number;
  imported_at: number;
  saved_connection_id: string | null;
}

export interface ImportFromBucketRequest {
  model_id: string;
  saved_connection_id: string;
  bucket?: string;
  object_path: string;
  format: BucketFormat;
  table_name?: string;
}

export interface ImportFromBucketResponse {
  table_id: string;
  source: BucketObjectWire;
}

export const importFromBucket = async (
  body: ImportFromBucketRequest,
): Promise<ImportFromBucketResponse> => {
  try {
    const res = await dataServiceApi.post<ImportFromBucketResponse>(
      "/tables/bucket/import",
      body,
    );
    return res.data;
  } catch (error) {
    log.error("importFromBucket failed:%o", error);
    throw mapError(error, "Failed to import object from bucket");
  }
};

export interface SyncBucketRequest {
  model_id: string;
  table_id: string;
  table_version: number;
  saved_connection_id: string;
  bucket?: string;
  object_path: string;
  format: BucketFormat;
  identity_decision?: IdentityDecision | null;
}

export interface SyncBucketResponse {
  /** Serialized TableSnapshot — pass straight to `handle_bucket_sync`. */
  updated_snapshot: unknown;
  updated_source: BucketObjectWire;
  schema_update: boolean;
  meta_update: boolean;
  data_version: number;
  no_change?: boolean;
  row_identity?: RowIdentity | null;
  sync_stats?: SyncStats | null;
}

/** Re-fetch the source object server-side and drop+recreate the table at a
 *  bumped TableVersion. Bucket tables are read-only; this is their only data
 *  mutation path. Mirrors `importFromBucket` but targets an existing table. */
export const syncBucketTable = async (
  body: SyncBucketRequest,
): Promise<SyncBucketResponse> => {
  try {
    const res = await dataServiceApi.post<SyncBucketResponse>(
      "/tables/bucket/sync",
      body,
    );
    return res.data;
  } catch (error) {
    log.error("syncBucketTable failed:%o", error);
    throw mapError(error, "Failed to sync object from bucket");
  }
};

export const inferBucketFormat = (path: string): BucketFormat | null => {
  const lower = path.toLowerCase();
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".tsv")) return "tsv";
  if (lower.endsWith(".parquet") || lower.endsWith(".pq")) return "parquet";
  return null;
};

function mapError(error: unknown, fallback: string): Error {
  if (axios.isAxiosError(error)) {
    const conflict = identityConflictFromBody(
      error.response?.status,
      error.response?.data,
    );
    if (conflict) {
      return new IdentityConflictError(conflict);
    }
    // Never `detail`: it carries diagnostic context that the backend logs but
    // shouldn't surface verbatim to users — see `server/src/api/buckets/errors.rs`.
    const headline = serverRefusalMessage(error.response?.data);
    if (headline) return new Error(headline);
    if (error.response?.status === 502) {
      return new Error("Cloud storage is unreachable right now. Try again.");
    }
  }
  return new Error(fallback);
}
